/**
 * #724 — whether the focused card can fire a two-sided S press right now, as
 * one pure answer shared by the key handler and the header readout.
 *
 * Before this module the listener and the readout each asked a different
 * question. The readout said "Ready A/D · S" whenever both sides had an
 * active price, while the listener also required post-only legs and a real
 * pair, so on every moneyline card the header advertised S and the key
 * refused it. One predicate means the header can only name S when a press
 * would get past these checks.
 *
 * What stays OUTSIDE this function, because it is not a property of the
 * card's targets: the DOM visibility fence (both price rows painted and on
 * screen), the per-press in-flight guard, an earlier S press that still has
 * an unconfirmed side, and the store's submit-time guard against the live
 * books. Those all refuse on their own, later, with their own reasons.
 *
 * Checks, in refusal order:
 *   1. (none for three-way games: on a soccer 1X2, S posts the same two team
 *      NOs A and D would, and the trader chooses that position knowingly);
 *   2. both sides show a price;
 *   3. both lines are active (source-unconfirmed wording matches the guard);
 *   4. both prices are bid joins, so each post-only leg rests;
 *   5. the pair is real: NO on two different team markets (moneyline), or
 *      YES and NO of one exact line (spread/total);
 *   6. on a two-outcome card, the two bids total less than 100¢: at least one
 *      leg pays 100¢ at settlement, so bids totalling 100¢ or more lose money
 *      when both fill. Not applied to a three-way game, where both team NOs
 *      pay on a draw, so their bids normally sum above 100¢ without a certain
 *      loss.
 */
import type { Lt2CardTargets, Lt2HotkeyTarget } from '@/stores/lt2FocusStore';
import {
  LINE_AVAILABILITY_LABEL,
  lineSourceUnconfirmed,
  type LineAvailability,
} from '@/lib/sport/lineAvailability';

export interface TwoSidedLeg {
  target: Lt2HotkeyTarget;
  ticker: string;
  priceCents: number;
}

export type TwoSidedReadiness =
  | { ok: true; a: TwoSidedLeg; d: TwoSidedLeg }
  | {
      ok: false;
      /** The leg label to report the refusal under, or null for the press. */
      legLabel: string | null;
      reason: string;
    };

/** The reason a non-active line refuses, in the words the submit guard uses:
 *  a line that is stale only because its source proof is withdrawn says so,
 *  rather than "Stale". */
export function lineRefusalReason(
  target: Pick<Lt2HotkeyTarget, 'book'>,
  availability: Exclude<LineAvailability, 'active'>,
): string {
  return availability === 'stale' && lineSourceUnconfirmed(target.book)
    ? 'Source confirmation unavailable'
    : LINE_AVAILABILITY_LABEL[availability];
}

/** `key` is the label of the key bound to the action (default "S"), so the
 *  refusal names the key the trader actually pressed after a remap. */
export function twoSidedReadiness(targets: Lt2CardTargets, key = 'S'): TwoSidedReadiness {
  const a = targets.a;
  const d = targets.d;
  if (a == null || d == null || a.priceCents == null || d.priceCents == null) {
    return { ok: false, legLabel: null, reason: `${key} needs a displayed price on both sides` };
  }
  for (const t of [a, d]) {
    if (t.availability !== 'active') {
      return {
        ok: false,
        legLabel: t.label,
        reason: `${lineRefusalReason(t, t.availability)} — ${key} not sent`,
      };
    }
  }
  if (!a.joinsBid || !d.joinsBid) {
    return {
      ok: false,
      legLabel: null,
      reason: `${key} posts at the bid on both sides — switch this board to Maker pricing`,
    };
  }
  const tickerA = a.book?.marketTicker ?? '';
  const tickerD = d.book?.marketTicker ?? '';
  const pairOk =
    targets.marketKind === 'moneyline'
      ? a.side === 'no' && d.side === 'no' && tickerA !== '' && tickerD !== '' && tickerA !== tickerD
      : tickerA !== '' && tickerA === tickerD && a.side !== d.side;
  if (!pairOk) {
    return { ok: false, legLabel: null, reason: `${key} has no two-sided pair on this card` };
  }
  if (targets.threeWay !== true && a.priceCents + d.priceCents >= 100) {
    return {
      ok: false,
      legLabel: null,
      reason: `${key} bids total ${a.priceCents + d.priceCents}¢ — both filling would lose money`,
    };
  }
  return {
    ok: true,
    a: { target: a, ticker: tickerA, priceCents: a.priceCents },
    d: { target: d, ticker: tickerD, priceCents: d.priceCents },
  };
}
